import { setHours, setMinutes, setSeconds } from 'date-fns'
import { ICreateReservationFormValues } from '../../../containers/Form/CreateReservation/CreateReservation'
import { toAHEX, toColor } from '../../../utils/ColorTransformator'
import { addOffsetDate } from '../../License/mappers/mapResponseToLicense'

type IRequestValues = ICreateReservationFormValues

function mapKidsAge(kidsAge) {
  const age = kidsAge.map(child => child.age.toString())
  for (let i = age.length; i < 4; i++) {
    age.push('0')
  }
  return JSON.stringify(age)
}

function toMidnight(date) {
  return setSeconds(setMinutes(setHours(date, 0), 0), 0)
}

export default function mapFormToRequest(
  calendarId: number,
  event: IRequestValues,
  id = '0',
): IRequestBody {
  return {
    /* eslint-disable @typescript-eslint/camelcase */
    additional_services: event.services
      ? event.services.map(service => ({
        first_tax: service.tax,
        price: service.price.toString().replace(',', '.'),
        second_tax: service.secondTax,
        service_name: service.name,
      }))
      : undefined,
    arrival_time: event.timeFrom,
    city: event.guest ? event.guest.city : undefined,
    client_email: event.guest ? event.guest.email : '',
    client_name: event.guest ? event.guest.name : undefined,
    client_phone: event.guest ? event.guest.phone : undefined,
    company: event.company
      ? {
        city: event.company.city,
        company_name: event.company ? event.company.name : undefined,
        country: event.company.country,
        post_code: event.company.postCode,
        street: event.company.street,
        vat_number: event.company.vatId,
      }
      : {},
    custom_color: event.setCustomColor
      ? toAHEX(toColor(event.customColor))
      : undefined,
    date_to_pay:
      event.timeToPayDate && (event.timeToPayDate as any) !== '0'
        ? Math.ceil(
        addOffsetDate(toMidnight(event.timeToPayDate)).getTime() / 1000,
        )
        : undefined,
    date_to_pay_after_start:
      event.timeToWaitDate && (event.timeToWaitDate as any) !== '0'
        ? Math.ceil(
        addOffsetDate(toMidnight(event.timeToWaitDate)).getTime() / 1000,
        )
        : undefined,
    date_to_pay_after_start_days: event.timeToWaitDays,
    date_to_pay_days: event.timeToPayDays ? event.timeToPayDays : undefined,
    deleted: 0,
    departure_time: event.timeTo,
    description: event.note,
    doubleBed: event.twinBeds,
    end_time: Math.floor(
      toMidnight(event.dateTo).getTime() / 1000 -
      toMidnight(event.dateTo).getTimezoneOffset() * 60,
    ),
    id_calendar: calendarId,
    id_card: event.guest ? event.guest.idCard : undefined,
    id_reservation: id,
    id_room: Number(event.roomId),
    kids: event.childrenCount,
    kids_age: event.children ? mapKidsAge(event.children) : undefined,
    local_number: event.guest ? event.guest.apartmentNumber : undefined,
    local_tax: event.localTax ? event.localTax.toString() : undefined,
    local_tax_included: event.withoutTax ? '1' : '0',
    nationality: event.guest ? event.guest.country : undefined,
    passport: event.guest ? event.guest.passport : undefined,
    payment_on_place_date: event.payOnPlaceDate
      ? Math.ceil(addOffsetDate(event.payOnPlaceDate).getTime() / 1000)
      : undefined,
    payment_on_place_method: event.payOnPlacePaymentType,
    persons: event.adultsCount,
    post_code: event.guest ? event.guest.postCode : undefined,
    prepayment_type: event.prepaymentType,
    price: event.endPrice ? event.endPrice.toString() : undefined,
    price_for_night: event.pricePerDay
      ? event.pricePerDay.toString()
      : undefined,
    price_type: event.priceType,
    provenance: event.provenances,
    provision: event.advancePaymentPrice
      ? event.advancePaymentPrice.toString()
      : undefined,
    provision_date: event.advancePriceDate
      ? Math.ceil(addOffsetDate(event.advancePriceDate).getTime() / 1000)
      : undefined,
    provision_payment_method: event.advancePaymentType,
    registration: Number(event.registration),
    room_key_code: event.doorCode,
    roommates: event.additionalGuests
      ? event.additionalGuests.map(guest => ({
        city: guest.city,
        client_name: guest.name,
        email: guest.email,
        id_card: guest.idCard,
        local_number: guest.apartmentNumber,
        nationality: guest.country,
        passport: guest.passport,
        phone: guest.phone,
        post_code: guest.postCode,
        street: guest.street,
        street_number: guest.houseNumber,
      }))
      : {},
    service: event.feeding,
    service_persons: Number(event.adultsCount) + Number(event.childrenCount),
    singleBed: event.singleBeds,
    stage: Number(event.reservationStatus),
    start_time: Math.floor(
      toMidnight(event.dateFrom).getTime() / 1000 -
      toMidnight(event.dateFrom).getTimezoneOffset() * 60,
    ),
    stay_deposit: event.deposit ? event.deposit.toString() : undefined,
    street: event.guest ? event.guest.street : undefined,
    street_number: event.guest ? event.guest.houseNumber : undefined,
    /* eslint-enable */
  }
}

export interface IRequestBody {
  id_calendar: number
  id_reservation: string
  id_room: number
  start_time: number
  end_time: number
  arrival_time?: string
  departure_time?: string
  stage?: number
  client_name?: string
  client_phone?: string
  client_email: string
  description?: string
  persons?: number
  kids?: number
  kids_age?: string
  service_persons?: number
  price?: string
  price_type?: number
  provision?: string
  stay_deposit?: string
  price_for_night?: string
  prepayment_type?: number
  service?: string
  room_key_code?: string
  registration?: number
  street?: string
  street_number?: string
  local_number?: string
  post_code?: string
  city?: string
  nationality?: string
  passport?: string
  id_card?: string
  provenance?: string
  local_tax?: string
  local_tax_included?: string
  provision_date?: number
  provision_payment_method?: string
  payment_on_place_date?: number
  payment_on_place_method?: string
  booking_id_reservation?: string
  icalendar_id_reservation?: string
  singleBed?: number
  doubleBed?: number
  page_size?: number
  page?: number
  deleted?: number
  date_to_pay?: number
  date_to_pay_days?: number
  date_to_pay_after_start?: number
  date_to_pay_after_start_days?: number
  custom_color?: string
  additional_services?: any
  company?: ICompany
  roommates?: IRoommate[] | {}
}

export interface IRoommate {
  client_name?: string
  street?: string
  street_number?: string
  local_number?: string
  post_code?: string
  city?: string
  nationality?: string
  passport?: string
  id_card?: string
}

export interface ICompany {
  company_name?: string
  street?: string
  city?: string
  post_code?: string
  country?: string
  vat_number?: string
}
